// Everything a tester who knows nothing about this demo needs in order to (a) hold up
// their end of the call and (b) tell whether Claire got it right.
//
// The agent reads the account live out of Supabase. This file is the SAME data as the
// seed, restated for the person on the other end of the line — so if a number here and a
// number Claire says disagree, that is a real finding, not a stale fixture. Keep it in
// sync with supabase/02-seed.sql.

export interface TierRow {
  label: string;
  platinum: string;
  gold: string;
  silver: string;
}

/**
 * The whole demo in one table. Two callers ask the identical question and hear two
 * credible, different answers — and neither is improvised, because every promise is read
 * out of `ftn_tier_policy` / `ftn_tier_benefits`. Claire is forbidden from inventing an
 * entitlement, so anything she offers that is not in this table is a bug.
 */
export const TIER_POLICY: TierRow[] = [
  { label: "First response",        platinum: "1 hour, 24/7",            gold: "4 business hours",              silver: "1 business day" },
  { label: "Returned payment retry", platinum: "Same day, on the call",  gold: "Next business day",             silver: "Standard weekly batch" },
  { label: "Returned payment fee",  platinum: "Waived on the call",      gold: "Courtesy waiver requested",     silver: "Reviewed, 2 days — never waived on the call" },
  { label: "Live human",            platinum: "Warm transfer to the named account manager", gold: "Priority callback", silver: "Logged case + email" },
  { label: "Integration re-transmit", platinum: "Same day, named engineer", gold: "Next business day",          silver: "Standard queue" },
  { label: "New location",          platinum: "5 days, fee waived",      gold: "10 days",                       silver: "15 days" },
  { label: "Custom GL coding",      platinum: "Yes",                     gold: "Yes",                           silver: "No" },
];

export type TierKey = "Platinum" | "Gold" | "Silver";

export function tierValue(row: TierRow, tier: string): string {
  return tier === "Platinum" ? row.platinum : tier === "Gold" ? row.gold : row.silver;
}

export interface Playbook {
  /** One-line framing of the situation, in the caller's own words. */
  situation: string;
  /** The first thing to say once Claire picks up. */
  opener: string;
  /** Lines to push with AFTER the first answer — these are where tier discipline shows. */
  followUps: string[];
  /** Ground truth Claire should be quoting. If her number differs, that is a finding. */
  facts: { label: string; value: string }[];
  /** What a CORRECT call looks like, in order. */
  expect: string[];
  /** Red flags. If you see one of these, the agent regressed. */
  watchOut: string[];
}

// Rules that apply on EVERY call regardless of who you are, so they are not repeated
// twelve times below. Surfaced in the UI alongside the per-caller list.
export const UNIVERSAL_WATCHOUTS = [
  "Any account detail before you have been verified — not the business name, not an amount, not even whether the account exists",
  "The service tier named more than once, or used as an opener or as flattery",
  "Comparing your plan to another one (\"on Platinum you'd get…\")",
  "A promise that is not in the tier table — no \"let me see what I can do\" implying an exception",
  "The same sentence said twice in one turn, or reassurance repeated on every turn",
];

export const VERIFICATION_NOTE =
  "Claire asks for your full name and the email on the account, in that order. Read the email back exactly as written — she will confirm it before looking anything up. Give a wrong surname on purpose and she must refuse without telling you which field failed.";

/** Keyed by FintechCustomer.id. */
export const FINTECH_PLAYBOOKS: Record<string, Playbook> = {
  // ─── Platinum ──────────────────────────────────────────────────────────────
  "100248": {
    situation:
      "Your bank returned Tuesday's draft. Nine invoices flipped back to unpaid and Southern Glazer's is now refusing tomorrow's delivery. You were just on chat about it.",
    opener:
      "Hi — my bank returned our payment this week and now our liquor distributor is saying they won't deliver tomorrow. I need this fixed today.",
    followUps: [
      "I want to talk to my account manager.",
      "Can you take the returned payment fee off?",
      "Will the delivery actually go out tomorrow?",
    ],
    facts: [
      { label: "Returned draft", value: "$38,412.65 — insufficient funds" },
      { label: "Invoices back to unpaid", value: "9" },
      { label: "Southern Glazer's", value: "$14,208.40 — inside the FL 10-day credit window" },
      { label: "Breakthru Beverage", value: "$9,642.85 — inside the FL 10-day credit window" },
      { label: "Account manager", value: "Rachel Dunn" },
    ],
    expect: [
      "Recognises the chat you just had — you should not have to explain it twice",
      "Quotes the real figures above, not round numbers",
      "Says the tier ONCE, at the moment it changes the outcome: same-day re-draft and the fee waived on the call",
      "Logs the case and reads the reference back digit by digit",
      "On \"I want my account manager\" — Platinum is the one tier with live transfer, so a brief acknowledgement then the hold line",
    ],
    watchOut: [
      "The fee offered vaguely (\"I'll see what I can do\") instead of confirmed as waived",
      "\"I'm transferring you to…\" from Claire — the hold line belongs to the transfer agent and is said once",
    ],
  },
  "100502": {
    situation:
      "Three days of invoice files have not landed in Sage Intacct and month-end close is Friday.",
    opener:
      "None of our invoice files have come through to Sage Intacct for three days, and we close the month on Friday.",
    followUps: ["Can someone actually own this today?", "What do I tell my controller about Friday?"],
    facts: [
      { label: "Southern Glazer's", value: "$31,420.75 — in transit" },
      { label: "US Foods", value: "$24,885.10 — in transit" },
      { label: "Outlets", value: "7, Biloxi MS" },
    ],
    expect: [
      "Treats this as an integration re-transmit, not a payment problem",
      "Platinum gets same-day re-transmit with a named engineer",
      "Case logged with the 1-hour first-response commitment",
    ],
    watchOut: ["A next-business-day promise — that is the Gold answer, not yours"],
  },
  "100788": {
    situation: "Your 12th property opens in three weeks and needs three distributors connected before it goes live.",
    opener:
      "We're opening our twelfth property in three weeks and I need three distributors connected before it goes live.",
    followUps: ["Is there an activation fee?", "Three weeks is tight — can you commit to that?"],
    facts: [
      { label: "Monday batch, 10 properties", value: "$102,885.40" },
      { label: "Properties live", value: "11, Denver CO" },
    ],
    expect: ["New location on Platinum: 5 days, activation fee waived — comfortably inside your three weeks"],
    watchOut: ["A 10- or 15-day timeline, which belongs to the other tiers"],
  },
  "101036": {
    situation: "You want beer split bottle vs draft, and spirits broken out per property in the GL coding.",
    opener:
      "I need our GL coding changed — beer split between bottle and draft, and spirits broken out by property.",
    followUps: ["Does that apply to invoices already in the system?"],
    facts: [
      { label: "Southern Glazer's", value: "$34,118.90 — in transit" },
      { label: "Hotels", value: "5, San Diego CA" },
    ],
    expect: [
      "Custom GL coding is available on Platinum, so this is a yes",
      "This topic is NOT tier-sensitive on the timeline, so the plan should not be mentioned at all",
    ],
    watchOut: ["The tier named anyway — if the policy row is null, Claire should stay quiet about the plan"],
  },

  // ─── Gold ──────────────────────────────────────────────────────────────────
  "100377": {
    situation:
      "You signed for a Reyes beer delivery four days ago, about $4,200, and it is still not in the portal.",
    opener:
      "I signed for a beer delivery four days ago, about forty two hundred dollars, and it's still not showing up in my portal. I don't want it going past my terms.",
    followUps: ["Can someone call me back today?", "Can I just send you a photo of the paper ticket?"],
    facts: [
      { label: "Reyes Beverage (missing)", value: "$4,268.40 — never transmitted, not in the portal" },
      { label: "Prior Reyes delivery", value: "$3,914.55 — came through normally" },
    ],
    expect: [
      "Confirms the invoice genuinely was not transmitted by the distributor — the fault is not yours",
      "Gold gets priority research OR the mobile-scan upload path, resolved in one business day",
      "On \"call me back today\" — a priority callback inside 4 business hours, stated as a fact",
    ],
    watchOut: ["A live transfer offered — Gold never gets one, however hard you push"],
  },
  "100589": {
    situation:
      "Wine is up about $6 a case versus your last four deliveries, plus split-case fees you did not expect.",
    opener:
      "My wine cases are about six dollars a case more than the last four deliveries, and there are split-case fees I wasn't expecting.",
    followUps: ["Who eats the difference?", "Can you get someone from Republic on this?"],
    facts: [{ label: "Republic National (disputed)", value: "$9,486.75" }, { label: "Stores", value: "4, Columbus OH" }],
    expect: ["Walks the price variance and the split-case fee separately", "Gold: 4-business-hour first response, case logged"],
    watchOut: ["Claire deciding the dispute herself, or promising a credit that is the distributor's call"],
  },
  "100701": {
    situation:
      "You are switching banks. A $9,118.75 draft sits inside the 2-3 day verification window, so it will still hit the OLD account.",
    opener: "We're changing our bank account — I need to update the details before the next draft.",
    followUps: ["So the next draft comes out of the new account, right?", "What if there isn't enough in the old one?"],
    facts: [
      { label: "Scheduled draft", value: "$9,118.75 — 2 days out, still hits the OLD account" },
      { label: "Verification window", value: "2-3 business days" },
    ],
    expect: [
      "Confirms you are an authorised contact BEFORE actioning anything",
      "Says clearly that the scheduled draft still hits the old account — this is the honest answer, not the comfortable one",
      "Refers to the account by last four digits only",
    ],
    watchOut: [
      "Implying the change lands in time — this is the scenario the agent is most likely to soften",
      "Full account numbers spoken back",
    ],
  },
  "100912": {
    situation: "Cash is tight before a festival weekend. You want the exact draft date and to pay one distributor early.",
    opener:
      "I need to know exactly when my next draft comes out, and whether I can pay one distributor early.",
    followUps: ["Can you move the draft date?"],
    facts: [{ label: "Weekly draft", value: "$5,240.60" }, { label: "Sites", value: "2, Grand Rapids MI" }],
    expect: [
      "Gives the exact draft date and walks the pay-early path",
      "Draft timing is not tier-sensitive, so the plan should not come up",
    ],
    watchOut: ["Agreeing to move the draft date if that is not something the platform actually does"],
  },

  // ─── Silver ────────────────────────────────────────────────────────────────
  "100415": {
    situation: "Two identical $12,304.18 ACH drafts hit your account on the same day.",
    opener: "You took the same payment twice — two identical drafts hit my account on the same day.",
    followUps: ["I need that reversed today.", "Just refund it, this is your error."],
    facts: [
      { label: "ACH batch", value: "$12,304.18 — settled" },
      { label: "Duplicate ACH batch", value: "$12,304.18 — the one in dispute" },
    ],
    expect: [
      "Confirms the duplicate from the data rather than asking you to prove it",
      "Silver: logged case + next-business-day email. No reversal promised on the call",
    ],
    watchOut: ["A same-day reversal promised — Silver never gets one, no matter how reasonable the request sounds"],
  },
  "100634": {
    situation:
      "Republic refused this morning's delivery. You are on the Louisiana state delinquent list. You thought this was paid weeks ago.",
    opener:
      "Republic just refused my delivery this morning. They're telling me I'm on some state delinquent list. I paid this weeks ago.",
    followUps: [
      "Just waive the fee, I've been a customer for three years.",
      "Get me a manager.",
      "When exactly will the state take me off the list?",
    ],
    facts: [
      { label: "Returned draft", value: "$6,842.30 — uncollected funds, 9 days ago, never cleared" },
      { label: "Republic National", value: "$4,218.90 — past the LA 30-day credit window" },
    ],
    expect: [
      "Explains plainly that the returned draft is the cause — the payment never cleared",
      "Silver: the retry goes with the standard weekly batch; the fee is REVIEWED, not waived on the call",
      "Offers the faster legitimate path — paying the distributor directly to clear the balance",
      "Compliance honesty: no promised date for the hold lifting. That is state law and the distributor's call",
      "Holds the line warmly on all three pushes — no waiver, no transfer",
    ],
    watchOut: [
      "A fee waived or a reversal promised",
      "A live transfer offered",
      "A guaranteed date for coming off the delinquent list",
      "Apologising for your tier, or telling you what another plan would have got you",
    ],
  },
  "100845": {
    situation: "You are locked out of the portal, the reset email is not arriving, and you want a bookkeeper added.",
    opener:
      "I'm locked out of the portal and the reset email never arrives. I also want to add my bookkeeper to the account.",
    followUps: ["Can you just reset it while I'm on the phone?"],
    facts: [{ label: "Weekly draft", value: "$1,962.45" }, { label: "Location", value: "Seattle WA" }],
    expect: ["Separates the lockout from the add-a-user request", "Silver: logged case + next-business-day email"],
    watchOut: ["A password or access reset performed live on the call"],
  },
  "101144": {
    situation:
      "You are under a state sales-tax audit and need 18 months of invoices. The portal only holds 15.",
    opener:
      "I'm under a state sales tax audit and I need eighteen months of invoices. Can you pull those for me?",
    followUps: ["I need all eighteen, not fifteen.", "How fast can I get them?"],
    facts: [
      { label: "Johnson Brothers", value: "$1,284.60 — inside the window" },
      { label: "Johnson Brothers", value: "$1,092.75 — ~17 months back, archive request" },
      { label: "Portal retention", value: "15 months" },
    ],
    expect: [
      "The honest answer is \"not entirely\": export for 15 months PLUS an archive request for the rest",
      "Silver: logged case + next-business-day email",
    ],
    watchOut: ["\"Sure, no problem\" for all 18 months — the portal does not hold them"],
  },
};

export function getPlaybook(customerId: string): Playbook | undefined {
  return FINTECH_PLAYBOOKS[customerId];
}
